import { useEnv } from '@/components/providers/env-provider';
import Fetch from '@/services/fetch/Fetch';
import { envToBaseUrl } from '@/utils/env';
import {
  InvalidateQueryFilters,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { UserType } from '@/shared-types/roleTypes';
import { Button } from '@/components/ui/button';
import { DialogHeader, DialogFooter } from '@/components/ui/dialog';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, PenIcon } from 'lucide-react';
import {
  FormField,
  FormItem,
  FormControl,
  FormDescription,
  FormMessage,
  FormLabel,
  Form,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Command,
} from '@/components/ui/command';
import { marketToLocales } from '@ingka-group-digital/pnv-shared';
import { MultiSelectCombobox } from '../form/formComponent/FormMultiSelect';

const NONE_VALUE = 'none';

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'This field has to be filled.' })
    .email('This is not a valid email.'),
  role: z.string(),
  marketRestrictions: z.array(z.string()).optional(),
});

const ROLES = [
  { label: 'Admin', value: 'admin' },
  { label: 'Creator', value: 'creator' },
  { label: 'Editor', value: 'editor' },
] as const;

export function ListUser() {
  const { env } = useEnv();
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [initialEmail, setInitialEmail] = useState('');
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState<UserType[] | undefined>([]);
  const endpoint = `${envToBaseUrl(env)}/roles/list`;

  const { data } = useQuery<UserType[]>({
    queryKey: ['list_all_users'],
    queryFn: () => Fetch.json(endpoint),
    refetchOnWindowFocus: false,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      role: '',
      marketRestrictions: [],
    },
  });

  const queryClient = useQueryClient();

  const deleteUserMutation = useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      setDeleteLoading(true);
      await Fetch.json(`${envToBaseUrl(env)}/roles/delete`, {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          role: data.role,
        }),
      });
    },
    onSuccess: async () => {
      toast.success('Successfully deleted user');
      await queryClient.invalidateQueries([
        'list_all_users',
      ] as InvalidateQueryFilters);
    },
    onError: () => {
      toast.error('Failed to delete user');
    },
    onSettled: () => {
      setDeleteLoading(false);
      setDeleteOpen(false);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (data: {
      email: string;
      role: string;
      marketRestrictions?: string[];
    }) => {
      setSubmitLoading(true);

      const newDataRestrictions = {
        ...data,
        marketRestrictions: data?.marketRestrictions?.includes(NONE_VALUE)
          ? []
          : data.marketRestrictions,
      };

      await Fetch.json(`${envToBaseUrl(env)}/roles/update`, {
        method: 'POST',
        body: JSON.stringify({
          initialEmail,
          ...newDataRestrictions,
        }),
      });
    },
    onSuccess: async () => {
      toast.success('Successfully updated user');
      await queryClient.invalidateQueries([
        'list_all_users',
      ] as InvalidateQueryFilters);
    },
    onError: () => {
      toast.error('Failed to update user');
    },
    onSettled: () => {
      setSubmitLoading(false);
      setOpen(false);
    },
  });

  const markets = useMemo(() => {
    const result = [{ label: 'None', value: NONE_VALUE }];
    for (const [key, value] of Object.entries(marketToLocales)) {
      const [, market] = value[0].split('-');
      result.push({
        label: key,
        value: market,
      });
    }

    return result;
  }, []);

  const handleDeleteUser = (data: UserType) => {
    deleteUserMutation.mutate(data);
  };
  const handleUpdateUser = (data: {
    email: string;
    role: string;
    marketRestrictions?: string[];
  }) => {
    updateUserMutation.mutate(data);
  };

  useEffect(() => {
    if (!search) {
      setFilteredData(data);
    }
    const newData = data?.filter((user) => {
      const emailLowercase = user.email.toLocaleLowerCase();
      const searchLowercase = search.toLocaleLowerCase();
      return emailLowercase.includes(searchLowercase);
    });
    setFilteredData(newData);
  }, [search, data]);

  // For existing users, when none is applied to market restrictions
  // its saved as an empty object in firestore, so we treat {} as none
  const getMarketLabel = (value?: string[]) => {
    if (!value?.length) {
      return ['none'];
    }
    return value;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
        <DialogContent className='w-full'>
          <DialogHeader>
            <DialogTitle>Edit user data</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleUpdateUser)}
              className='space-y-6'
            >
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder='email' {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='role'
                render={({ field }) => (
                  <FormItem className='flex flex-col'>
                    <FormLabel>Role</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant='outline'
                            role='combobox'
                            className={cn(
                              'w-full',
                              !field.value && 'text-muted-foreground',
                              '[&>span]:w-full [&>span]:justify-between',
                            )}
                          >
                            {field.value
                              ? ROLES.find((role) => role.value === field.value)
                                  ?.label
                              : 'Select role'}
                            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className='w-[200px] p-0'>
                        <Command>
                          <CommandInput placeholder='Search role...' />
                          <CommandList>
                            <CommandEmpty>No role found.</CommandEmpty>
                            <CommandGroup>
                              {ROLES.map((role) => (
                                <CommandItem
                                  value={role.label}
                                  key={role.value}
                                  onSelect={() => {
                                    form.setValue('role', role.value);
                                    if (role.value !== 'editor') {
                                      form.reset({
                                        ...form.getValues(),
                                        marketRestrictions: [],
                                      });
                                    }
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      role.value === field.value
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {role.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      Read more about roles
                      https://confluence.build.ingka.ikea.com/display/POI/Configurator+-+Roles
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch('role') === 'editor' && (
                <FormField
                  control={form.control}
                  name='marketRestrictions'
                  render={({ field }) => (
                    <MultiSelectCombobox
                      items={markets}
                      selectedValues={getMarketLabel(field.value) || []}
                      onSelectionChange={field.onChange}
                      placeholder='Select market restrictions'
                      description='Restrict user access to specific markets. Set to none if user should have global access.'
                      disabled={false}
                      searchEnabled
                      searchPlaceholder='Search market'
                    />
                  )}
                />
              )}
              <Button type='submit' isLoading={submitLoading}>
                Submit
              </Button>
            </form>
          </Form>
          <DialogFooter className='sm:justify-end'></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteOpen} onOpenChange={(open) => setDeleteOpen(open)}>
        <DialogContent className='w-full'>
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
          </DialogHeader>
          Are you sure you wish to delete this user?
          <DialogFooter>
            <Button
              variant='default'
              isLoading={deleteLoading}
              onClick={() => handleDeleteUser(form.getValues() as UserType)}
            >
              Yes
            </Button>
            <Button variant='default' onClick={() => setDeleteOpen(false)}>
              No
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {filteredData && (
        <Input
          placeholder='Search by email'
          onChange={(e) => setSearch(e.target.value)}
          className='mb-2'
        />
      )}
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-[100px]'>User</TableHead>
              <TableHead>Market restriction</TableHead>
              <TableHead>Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData?.map((role, index) => (
              <TableRow key={`${role.email}-${index}`}>
                <TableCell className='font-medium'>{role.email}</TableCell>
                <TableCell>
                  {role.role === 'editor' && !role.restrictions?.markets
                    ? 'None'
                    : role.restrictions.markets?.join(', ')}
                </TableCell>
                <TableCell>{role.role}</TableCell>
                <TableCell className='items-center flex'>
                  <Button
                    variant='default'
                    className='mr-2'
                    onClick={() => {
                      setDeleteOpen(true);
                      form.reset({
                        email: role.email,
                        role: role.role,
                        marketRestrictions: role.restrictions.markets,
                      });
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setOpen(true);
                      setInitialEmail(role.email);
                      form.reset({
                        email: role.email,
                        role: role.role,
                        marketRestrictions: role.restrictions?.markets || [],
                      });
                    }}
                  >
                    <PenIcon className={`h-4 w-4`} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
